/**
 * ProviderIntegrationSection - Seção de integração com gateway de pagamento
 * 
 * Exibida no formulário de Conta Corrente quando tipo_conta = 'pagamento' e banco = Asaas (461).
 * Permite configurar API key, ambiente (sandbox/produção), testar conexão e
 * registrar/re-registrar webhook manualmente.
 * 
 * RELEASE: 2026-03-14 v1.0.0
 * RELEASE: 2026-03-15 v1.1.0 - Alerta de webhook não configurado + botão de retry
 * @author Módulo Financeiro - Integração Multi-Provider
 */

import { useState } from 'react';
import { Wifi, WifiOff, Loader2, CheckCircle2, XCircle, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('ProviderIntegration');

interface ProviderIntegrationSectionProps {
  /** ID da conta corrente (null se criação) */
  contaCorrenteId: string | null;
  /** Código do banco selecionado */
  bancoCodigo: string;
  /** Tipo de conta selecionado */
  tipoConta: string;
  /** Status atual de conexão */
  isConnected?: boolean;
  /** Provider slug atual */
  currentProviderSlug?: string | null;
  /** Ambiente atual */
  currentEnvironment?: string | null;
  /** ID da conta no provider */
  providerAccountId?: string | null;
  /** Indica se o webhook está configurado corretamente */
  webhookConfigured?: boolean;
  /** Callback para atualizar status do webhook após registro bem-sucedido */
  onWebhookRegistered?: () => void;
}

/**
 * Seção de integração com gateway de pagamento
 * Visível apenas quando tipo_conta = 'pagamento' e banco = Asaas (461)
 */
export function ProviderIntegrationSection({
  contaCorrenteId,
  bancoCodigo,
  tipoConta,
  isConnected = false,
  currentProviderSlug,
  currentEnvironment,
  providerAccountId,
  webhookConfigured = false,
  onWebhookRegistered,
}: ProviderIntegrationSectionProps) {
  const [apiKey, setApiKey] = useState('');
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>(
    (currentEnvironment as 'sandbox' | 'production') || 'sandbox'
  );
  const [showApiKey, setShowApiKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [registeringWebhook, setRegisteringWebhook] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Só exibir para conta tipo pagamento com banco Asaas (461)
  if (tipoConta !== 'pagamento' || bancoCodigo !== '461') {
    return null;
  }

  // Determinar provider pelo banco
  const providerSlug = 'asaas';

  /**
   * Testa conexão com o gateway usando a API key informada
   */
  const handleTestConnection = async () => {
    if (!apiKey.trim()) {
      toast.error('Informe a API key para testar');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('financial-provider', {
        body: {
          action: 'test_connection',
          provider_slug: providerSlug,
          conta_corrente_id: contaCorrenteId || '',
          api_key: apiKey,
          environment,
        },
      });

      if (error) throw error;

      if (data?.success) {
        setTestResult({ success: true, message: 'Conexão estabelecida com sucesso!' });
        toast.success('Conexão com Asaas estabelecida!');
      } else {
        setTestResult({ success: false, message: data?.error || 'Falha na conexão' });
        toast.error(data?.error || 'Falha no teste de conexão');
      }
    } catch (err) {
      logger.error('Erro no teste de conexão', { error: err });
      setTestResult({ success: false, message: 'Erro ao conectar com o gateway' });
      toast.error('Erro ao testar conexão');
    } finally {
      setTesting(false);
    }
  };

  /**
   * Salva credenciais e vincula à conta corrente
   */
  const handleSaveCredentials = async () => {
    if (!apiKey.trim()) {
      toast.error('Informe a API key para salvar');
      return;
    }

    if (!contaCorrenteId) {
      toast.error('Salve a conta corrente primeiro antes de configurar a integração');
      return;
    }

    setSaving(true);

    try {
      const { data, error } = await supabase.functions.invoke('financial-provider', {
        body: {
          action: 'save_credentials',
          provider_slug: providerSlug,
          conta_corrente_id: contaCorrenteId,
          api_key: apiKey,
          environment,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success('Integração configurada com sucesso!');
        setApiKey(''); // Limpar campo por segurança
        setTestResult({ success: true, message: 'Integração ativa' });
        // Notificar parent sobre status do webhook
        if (data.webhook_registered) {
          onWebhookRegistered?.();
        }
      } else {
        toast.error(data?.error || 'Erro ao salvar credenciais');
      }
    } catch (err) {
      logger.error('Erro ao salvar credenciais', { error: err });
      toast.error('Erro ao salvar credenciais');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Registra (ou re-registra) webhook manualmente no Asaas
   * Utilizado quando o registro automático falhou silenciosamente
   */
  const handleRegisterWebhook = async () => {
    if (!contaCorrenteId) {
      toast.error('Conta corrente não salva');
      return;
    }

    setRegisteringWebhook(true);

    try {
      const { data, error } = await supabase.functions.invoke('financial-provider', {
        body: {
          action: 'register_webhook',
          provider_slug: providerSlug,
          conta_corrente_id: contaCorrenteId,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success('Webhook configurado com sucesso! O sistema agora receberá notificações de pagamento.');
        onWebhookRegistered?.();
      } else {
        toast.error(data?.error || 'Falha ao registrar webhook');
      }
    } catch (err) {
      logger.error('Erro ao registrar webhook', { error: err });
      toast.error('Erro ao registrar webhook');
    } finally {
      setRegisteringWebhook(false);
    }
  };

  return (
    <div className="border border-border rounded-lg p-4 space-y-4 bg-muted/30">
      {/* Header da seção */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isConnected ? (
            <Wifi className="h-4 w-4 text-green-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-sm font-medium text-foreground">Integração Asaas</span>
        </div>
        <Badge
          variant={isConnected ? 'default' : 'outline'}
          className={isConnected
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : ''
          }
        >
          {isConnected ? 'Conectado' : 'Desconectado'}
        </Badge>
      </div>

      {/* Informações da conexão ativa */}
      {isConnected && providerAccountId && (
        <div className="text-xs text-muted-foreground">
          Conta: {providerAccountId} | Ambiente: {currentEnvironment === 'production' ? 'Produção' : 'Sandbox'}
        </div>
      )}

      {/* Alerta de webhook não configurado */}
      {isConnected && !webhookConfigured && (
        <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-900/20">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-amber-700 dark:text-amber-300 text-xs ml-2">
            <strong>Webhook não configurado</strong> — o sistema não receberá notificações automáticas de pagamento do Asaas.
            <div className="mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRegisterWebhook}
                disabled={registeringWebhook}
                className="border-amber-500/50 text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40"
              >
                {registeringWebhook ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Configurando...</>
                ) : (
                  <><Wifi className="h-3.5 w-3.5 mr-1.5" /> Configurar Webhook</>
                )}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Ambiente */}
      <div className="flex items-center justify-between">
        <Label htmlFor="environment" className="text-sm">
          Ambiente de {environment === 'production' ? 'Produção' : 'Sandbox'}
        </Label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Sandbox</span>
          <Switch
            id="environment"
            checked={environment === 'production'}
            onCheckedChange={(checked) => setEnvironment(checked ? 'production' : 'sandbox')}
          />
          <span className="text-xs text-muted-foreground">Produção</span>
        </div>
      </div>

      {/* API Key */}
      <div>
        <Label htmlFor="api_key" className="text-sm">API Key Asaas</Label>
        <div className="relative mt-1">
          <Input
            id="api_key"
            type={showApiKey ? 'text' : 'password'}
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder={isConnected ? '••••••••••••••••' : 'Cole sua API key aqui'}
            className="pr-10"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => setShowApiKey(!showApiKey)}
          >
            {showApiKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Resultado do teste */}
      {testResult && (
        <div className={`flex items-center gap-2 text-xs p-2 rounded ${
          testResult.success 
            ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
            : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
        }`}>
          {testResult.success ? (
            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
          ) : (
            <XCircle className="h-3.5 w-3.5 flex-shrink-0" />
          )}
          {testResult.message}
        </div>
      )}

      {/* Botões de ação */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleTestConnection}
          disabled={testing || !apiKey.trim()}
        >
          {testing ? (
            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Testando...</>
          ) : (
            <><Wifi className="h-3.5 w-3.5 mr-1.5" /> Testar Conexão</>
          )}
        </Button>
        <Button
          size="sm"
          onClick={handleSaveCredentials}
          disabled={saving || !apiKey.trim() || !contaCorrenteId}
        >
          {saving ? (
            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Salvando...</>
          ) : (
            'Salvar Integração'
          )}
        </Button>
      </div>

      {/* Aviso de conta não salva */}
      {!contaCorrenteId && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Salve a conta corrente primeiro para configurar a integração.
        </p>
      )}
    </div>
  );
}
